import config from '@/config/config';
import { get } from '@/helpers/api.helper';
import type { MaterialInfo } from '@/constants/materialTypeConfig';

const { matching_api } = config.api;

export const getVendorByPath = async ({ vendorPath }: { vendorPath: string }) => {
    const { data } = await get(`${matching_api}/common/search/vendors`, {
        params: {
            filter: `vendorPath||$eq||${vendorPath}`,
        },
    });
    return data as Vendor[];
};

export const getFilterOptionImages = async ({ mallProductGroupId }: { mallProductGroupId: string }) => {
    const { data } = await get(`${matching_api}/filters/options/images/mallProductGroupId/${mallProductGroupId}`);
    return data as OptionImage[];
};

// 상품 리스트 조회
export const getProductList = async ({ page, limit, filter }: { page: number; limit: number; filter: string }) => {
    const { data } = await get(`${matching_api}/web-vendor/products2?${filter}`, {
        params: {
            page,
            limit,
        },
    });
    return data as { data: Product[]; count: number; page: number; pageCount: number; total: number };
};

export const getProductDetail = async ({ productId }: { productId: string }) => {
    const { data } = await get(`${matching_api}/web-vendor/products2/${productId}`);
    return data as Product;
};

export const getCategories = async (type?: 'MAIN' | 'SUB'): Promise<Category[]> => {
    const categoryType = type || 'MAIN';
    try {
        return await get(`${matching_api}/common/category?type=${categoryType}`).then((r) => {
            return r.data.items;
        });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export type Vendor = {
    id: string;
    nickname: string;
    email: string;
    imageUrl: string;
    vendorPath: string;
    description: string;
    shop3Banner?: { image: string; url?: string }[];
    vendorBusinessInfo: {
        id: string;
        operatorType: string;
        taxationType: null;
        businessType: string;
        businessItem: string;
        settleBank: string;
        settleAccountNumber: string;
        settleBankBookUrl: string;
        settleAccountOwnerName: string;
        businessRegistrationNumber: string;
        businessRegistrationCertificateUrl: string;
        mailOrderSalesRegistrationNumber: string;
        mailOrderSalesRegistrationCertificateUrl: string;
        additionalCertificateUrl: any[];
        interestCategories: string[];
        ownerName: string;
        companyName: string;
        companyAddress: string;
        managerPhoneNumber: string;
        customerCenterNumber: string;
        termsAgreedAt: null;
        updatedAt: string;
        createdAt: string;
        shippingReturnAddress: {
            postCode: string;
            recipientAddress: string;
            recipientAddressDetail: string;
        };
        managerName: string;
        verifyBusinessInfo: 'f' | 't' | null;
        verifyAccountInfo: 'f' | 't' | null;
        storeLocation: string | null;
        storeName: string | null;
        snsInfo: {
            platform: string;
            url: string;
            name: string;
        }[];
    };
};

export type Category = {
    categoryId: string;
    categoryName: string;
    imageUrl: string;
    parentCategoryId: string;
    sortOrder: number;
    wholeCategoryId: string;
    wholeCategoryName: string;
};

export type Product = {
    id: string;
    vendorId: string;
    representProductId: string;
    categoryId: string;
    groupTitle: string;
    representImageUrl: string;
    tags: [];
    description: string;
    salesPolicyDescription: string;
    createdAt: string;
    groupImageUrl: string[];
    // 제품 설명 섹션에 대표/그룹 이미지 노출 여부 및 위치
    useThumbnailInDetail?: 't' | 'f' | null;
    thumbnailInDetailPosition?: 'top' | 'bottom' | null;
    optionShowOrder: string[];
    productInfo: {
        product_id: string;
        options: {
            optionKey?: string;
            option_key?: string;
            optionValue?: string;
            option_value?: string;
        }[];
    }[];
    productDocuments: [
        {
            list: string[];
            name: string;
            listId: string;
        },
    ];
    isPublic: string;
    shippingReturnAddressBookId: string;
    onlineMinPriceLink: string;
    isPrimaryProduct: string;
    followerCutoffMin: number;
    manufacturer: string;
    countryOfOrigin: string;
    manufactureDate: string;
    afterServiceManager: string;
    afterServicePhone: string;
    categoryTags: string[];
    productCode: string | null;
    isSetProduct: string;
    setSelectionCount: number;
    optionSelectorType: string;
    fabricInfo: {
        lining: string;
        thickness: string;
        elasticity: string;
        seeThrough: string;
    };
    washInfo: {
        dry: string;
        iron: string;
        bleach: string;
        method: string;
        special: string;
        etc: string[];
    };
    materialInfo: MaterialInfo[] | null;
    colorNameInfo: {
        [key: string]: string;
    };
    sizeInfo: { size: string; [key: string]: string }[];
    sizeType: string;
    optionInputType: string;
    matchingProducts: ProductOption[];
    matchingSponsorshipCondition: {
        id: string;
        conditionType: string | null;
        condition: string;
        managerName: string;
        managerPhone: string;
        managerEmail: string;
        matchingProductGroupId: string;
        createdAt: string;
        shippingReturnAddress: {
            recipientAddressDetail: string;
        };
    };
    vendor: {
        id: string;
        nickname: string;
        imageUrl: string;
    };
    singleUnitAvailable?: 't' | 'f' | null;
};

export type ProductOption = {
    id: string;
    optionName: string;
    inventory: number;
    supplyPrice: number;
    marketPrice: number;
    discountMethod: string;
    discountValue: number;
    productCharge: number;
    isPublic: string;
    createdAt: string;
    isActivated: string;
    marginRate: number;
    productCode: string | null;
    optionImageUrl: string | null;
    color: string | null;
};

export type OptionImage = {
    optionBaseImageId: string;
    optionBaseImageUrl: string;
    options: Option[];
};

export type Option = {
    optionId: string;
    optionImageId: string;
    optionValue: string;
    optionDescription: string;
    optionImageUrl: string;
};

export type WashInfo = {
    dry: string;
    iron: string;
    bleach: string;
    method: string;
    special: string;
    etc: string[];
};

export type DimEntity = {
    id: string;
    name: string;
    sortOrder: number;
};

export type DimEntitiesResponse = {
    dimFit: DimEntity[];
    dimPattern: DimEntity[];
    dimSeason: DimEntity[];
    dimSheerness: DimEntity[];
    dimStretch: DimEntity[];
    dimStyle: DimEntity[];
    dimThickness: DimEntity[];
    dimLining?: DimEntity[];
    dimDetail: DimEntity[];
};

export const getDimEntities = async (): Promise<DimEntitiesResponse> => {
    const { data } = await get(`${matching_api}/web-vendor/products/dim-entities`);
    return data as DimEntitiesResponse;
};
